import { useEffect, useRef, useState } from 'react';

interface ParallaxOptions {
  speed?: number; // Multiplier for parallax effect (0.5 = half speed, 2 = double speed)
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean; // Disable on mobile
}

export const useParallax = (options: ParallaxOptions = {}) => {
  const { speed = 0.5, direction = 'vertical', disabled = false } = options;
  const elementRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (disabled) return;

    const handleScroll = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const scrolled = window.scrollY;
      const elementTop = rect.top + scrolled;
      const windowHeight = window.innerHeight;

      // Calculate offset based on element position relative to viewport
      const elementCenter = elementTop + rect.height / 2;
      const viewportCenter = scrolled + windowHeight / 2;
      const distance = viewportCenter - elementCenter;

      setOffset(distance * speed);
    };

    handleScroll(); // Initial calculation
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [speed, disabled]);

  const transform = direction === 'vertical' 
    ? `translate3d(0, ${offset}px, 0)` 
    : `translate3d(${offset}px, 0, 0)`;

  return {
    ref: elementRef,
    style: {
      transform,
      willChange: 'transform',
    },
  };
};
